import {
  Input,
  inject,
  signal,
  OnInit,
  Output,
  OnChanges,
  QueryList,
  Component,
  DestroyRef,
  EventEmitter,
  ViewChildren,
  SimpleChange,
  SimpleChanges,
  ViewContainerRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  FormArray,
  FormGroup,
  FormControl,
  AbstractControl,
} from '@angular/forms';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';

import { Store } from '@ngrx/store';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { DialogCloseReason, DialogService } from '@sui/dialog';

import { LoggerService } from '@services/logger.service';
import { UtilsDataObjectApiService } from 'src/app/api/services/utils-data-object-api.service';
import { UtilsService } from 'src/app/core/services/utils.service';
import { UserObjectsService } from 'src/app/core/services/user-objects.service';

import { TabChangeEvent } from '../form-tabs/form-tabs.component';
import { PickerDataobjectsDialogComponent } from 'src/app/core/components/popups/picker-dataobjects-dialog/picker-dataobjects-dialog.component';

import { Language, ObjectDefinition } from '../../../core/models/core.models';
import {
  FormProperty,
  PropertyGroup,
  FormControlItem,
} from '../../../core/models/form.models';
import {
  PopupType,
  NomLanguages,
  NomObjectClass,
} from '../../../core/constants';

import { selectUser } from '../../../core/reducers';
import { AppState } from 'src/app/reducers';

import {
  tap,
  map,
  take,
  filter,
  switchMap,
  Observable,
  lastValueFrom,
  BehaviorSubject,
} from 'rxjs';

interface TabState {
  [tabIndex: number]: string;
}

interface StructuredMetricItem {
  property_uri: string;
  order: number;
  selected: boolean;
}
@Component({
  selector: 'app-dynamic-form',
  templateUrl: './dynamic-form.component.html',
  styleUrls: ['./dynamic-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class DynamicFormComponent implements OnInit, OnChanges {
  @Input() formProperties: FormProperty[] = [];
  @Input() formGroup: FormGroup;
  @Input() fieldFilterString: string = '';
  @Input() scrollableColumns: boolean = false;
  @Input() isFilterEnabled: boolean = true;
  @Input() searchString$: BehaviorSubject<string>;

  @Output() onFormLanguageChanged = new EventEmitter<Language>();

  @ViewChildren('dynamicComponentContainer', { read: ViewContainerRef })
  dynamicComponentContainers: QueryList<ViewContainerRef>;

  hasDynamicValidation = false;
  defaultGroup: PropertyGroup = {
    key: 'dynamic-form.group.default',
    style: null,
  };
  propertyGroups: Array<any>;
  maxColumns: number;

  currentLanguageId: NomLanguages = NomLanguages.DEFAULT;
  tabState: TabState = {};

  maxGroupRow = 0;
  maxGroupCol = 0;

  groupRows: number[] = [];
  groupCols: number[] = [];

  checkAllSelected: boolean = false;

  private destroyRef = inject(DestroyRef);
  private dialogService = inject(DialogService);
  private utilsDataObjectApi = inject(UtilsDataObjectApiService);
  private userObjects = inject(UserObjectsService);
  private utilsService = inject(UtilsService);
  /** Password controls whose value is shown as text (additionalConfig.revealable). */
  readonly passwordRevealed = signal<Record<string, boolean>>({});

  constructor(
    private store: Store<AppState>,
    private logger: LoggerService,
  ) {}

  togglePasswordReveal(propertyName: string): void {
    this.passwordRevealed.update((state) => ({
      ...state,
      [propertyName]: !state[propertyName],
    }));
  }

  /** Copies the control's current value (additionalConfig.copyable); the shared service shows the toast. */
  copyFieldValue(propertyName: string): void {
    this.utilsService.copyToClipboard(
      String(this.formGroup.get(propertyName)?.value ?? ''),
    );
  }

  ngOnInit() {
    this.formProperties.forEach((prop) => {
      if (prop.formChanger) {
        this.hasDynamicValidation = true;
        this.formGroup
          .get([prop.propertyName])
          .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((value) => {
            this.estimateFormControlsValidators();
          });
      }
    });

    this.formProperties.forEach((prop) => {
      if (
        prop.controlType === 'checkbox-array' &&
        prop.additionalConfig?.['enableSelectAll']
      ) {
        const control = this.formGroup.get([prop.propertyName]);

        if (control) {
          this.updateCheckAllState(
            control.value,
            prop?.additionalConfig?.['values'],
          );

          control.valueChanges
            .pipe(
              tap(() => {
                setTimeout(() => {
                  this.updateCheckAllState(
                    control.value,
                    prop?.additionalConfig?.['values'],
                  );
                }, 0);
              }),
              takeUntilDestroyed(this.destroyRef),
            )
            .subscribe();
        }
      }
    });

    this.setupPickers();
  }

  ngAfterViewInit() {
    //this.loadDynamicComponents();
    // Subscribe to changes so that when the ngIf condition becomes true, you are notified
    this.dynamicComponentContainers.changes
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((containers: QueryList<ViewContainerRef>) => {
        if (containers.length) {
          this.loadDynamicComponents();
        }
      });
  }

  loadDynamicComponents() {
    this.dynamicComponentContainers?.forEach((container, index) => {
      const prop = this.formProperties[index];

      // Ensure the prop has 'componentType' defined
      this.logger.debug('prop', prop);
      if (prop.controlType === 'component') {
        let inputs = prop.additionalConfig['inputs'] || {};
        let inputs$: Observable<any> = null;
        if (prop.additionalConfig['inputProvider']) {
          inputs = prop.additionalConfig['inputProvider']()();
        }

        if ('inputProviderAsync' in prop.additionalConfig) {
          inputs$ = prop.additionalConfig['inputProviderAsync']();
        }
        prop.compnentInstance = this.createDynamicComponent(
          prop.additionalConfig['component'],
          container,
          inputs,
          prop.additionalConfig['outputs'],
          inputs$,
        );
      }
    });
  }

  createDynamicComponent(
    component: any,
    container: ViewContainerRef,
    inputs?: any,
    outputs?: { [key: string]: Function },
    inputs$: Observable<any> = null,
  ): any {
    //console.log('createDynamicComponent', component, inputs, outputs);
    container.clear(); // Clear any previous components in the container

    // Create the component and capture the component reference
    const componentRef = container.createComponent(component);

    // Assign inputs to the component instance if inputs are provided
    if (inputs) {
      Object.keys(inputs).forEach((key) => {
        componentRef.instance[key] = inputs[key];
      });
    }
    componentRef.instance['searchString$'] = this.searchString$;
    // Manually trigger change detection on the new component
    componentRef.changeDetectorRef.detectChanges();

    // Subscribing to the inputs Observable
    if (inputs$) {
      inputs$.pipe(take(1)).subscribe((inputs) => {
        const changes = {};
        Object.keys(inputs).forEach((key) => {
          // Prepare a SimpleChange object for each input
          changes[key] = new SimpleChange(
            componentRef.instance[key],
            inputs[key],
            false,
          );
          // Update the input property
          componentRef.instance[key] = inputs[key];
        });

        // Manually trigger ngOnChanges with the changes object
        if (
          componentRef.instance['ngOnChanges'] &&
          Object.keys(changes).length > 0
        ) {
          componentRef.instance['ngOnChanges'](changes);
        }

        componentRef.changeDetectorRef.detectChanges();
      });
    }

    // Subscribe to component outputs
    if (outputs) {
      Object.keys(outputs).forEach((key) => {
        if (componentRef.instance[key]) {
          componentRef.instance[key].subscribe(outputs[key]);
        }
      });
    }

    return componentRef.instance;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['formProperties']) {
      this.propertyGroups = this.groupAndOrganizeProperties(
        this.formProperties,
      );
    }
    if (changes['formGroup']) {
      this.estimateFormControlsValidators();
    }
  }

  private groupAndOrganizeProperties(properties: FormProperty[]): any {
    let groupedProperties = properties.reduce((acc, prop) => {
      const group: PropertyGroup = prop.propertyGroup || this.defaultGroup;
      if (!acc[group.key]) {
        acc[group.key] = {
          col: prop.propertyGroupCol || 0,
          row: prop.propertyGroupRow || 0,
          key: group.key,
          style: group.style ?? null,
          properties: [],
        };

        this.maxGroupCol = Math.max(this.maxGroupCol, acc[group.key].col);
        this.maxGroupRow = Math.max(this.maxGroupRow, acc[group.key].row);
      }
      acc[group['key']]['properties'].push(prop);
      return acc;
    }, {});

    Object.keys(groupedProperties).forEach((group) => {
      groupedProperties[group]['properties'] = this.organizeIntoColumns(
        groupedProperties[group]['properties'],
      );
    });

    this.groupRows = Array.from(
      { length: this.maxGroupRow + 1 },
      (_, index) => index,
    );
    this.groupCols = Array.from(
      { length: this.maxGroupCol + 1 },
      (_, index) => index,
    );
    const entries = Object.values(groupedProperties);
    // Sort and organize the properties within each group by rows and columns
    entries.sort((aValue, bValue) => {
      const a = aValue as { row?: number; col?: number };
      const b = bValue as { row?: number; col?: number };
      const rowDiff = (a.row || 0) - (b.row || 0);
      if (rowDiff !== 0) return rowDiff;
      return (a.col || 0) - (b.col || 0);
    });

    // Iterate through each group and check if it has any visible items
    entries.forEach(async (group: any) => {
      group.hasVisibleItems = await this.estimateGroupVisibility(group);
      //console.log('hasVisibleItem', hasVisibleItem);
    });
    // console.log('Entries', entries);
    return entries;
  }

  onToggleButtonsChange(event, property: FormProperty) {
    if (property.additionalConfig?.['onToggleButtonsChange']) {
      property.additionalConfig['onToggleButtonsChange'](event);
    }
  }

  // This async function returns a Promise<boolean> based on the async and sync checks.
  private async estimateGroupVisibility(group: any): Promise<boolean> {
    // Flatten nested properties arrays
    const flattenedProperties = group.properties.flat(Infinity);

    // Loop over properties and if any property indicates visibility, return true.
    for (const prop of flattenedProperties) {
      let isVisible = false;

      // Use async check if available.
      if (typeof prop.shouldDisplayAsync === 'function') {
        // Await the observable's result.
        isVisible = await lastValueFrom(
          prop.shouldDisplayAsync(this.formGroup),
        );
      }
      // Otherwise, check synchronously.
      else if (typeof prop.shouldDisplay === 'function') {
        isVisible = prop.shouldDisplay(this.formGroup);
      }
      // Default to true if no check is provided.
      else {
        isVisible = true;
      }
      this.logger.debug(
        'Property',
        prop.propertyName,
        'isVisible',
        isVisible,
        'group',
        group.groupLabel,
        'groupRow',
        group.row,
        'groupCol',
        group.col,
      );

      // As soon as one property is visible, the group is visible.
      if (isVisible) {
        return true;
      }
    }
    return false;
  }

  // Update all groups with their calculated visibility.
  public async estimateFormGroupsVisibility(): Promise<void> {
    for (const group of this.propertyGroups) {
      group.hasVisibleItems = await this.estimateGroupVisibility(group);
    }
  }

  private organizeIntoColumns(properties: FormProperty[]): FormProperty[][] {
    const maxColumnIndex = properties.reduce(
      (max, prop) => Math.max(max, prop.columnIndex || 0),
      0,
    );
    const columns = Array.from({ length: maxColumnIndex + 1 }, () => []);

    let lineProperties: FormProperty[] = [];
    let columnIndex = 0;
    properties.forEach((prop) => {
      columnIndex = prop.columnIndex || 0;
      if (prop.inlineBreak) {
        columns[columnIndex].push(lineProperties);
        lineProperties = [];
      }
      if (prop.inline) {
        lineProperties.push(prop);
      } else {
        if (lineProperties.length > 0) {
          columns[columnIndex].push(lineProperties);
          lineProperties = [];
        }

        columns[columnIndex].push([prop]);
      }
      //columns[columnIndex].push(prop);
    });
    if (lineProperties.length > 0) {
      columns[columnIndex].push(lineProperties);
      lineProperties = [];
    }

    return columns;
  }

  getLanguageKeys(propertyNameLanguages: any): string[] {
    return propertyNameLanguages ? Object.keys(propertyNameLanguages) : [];
  }

  hasFilteredItems(group: any[], filterString: string): boolean {
    if (filterString.length === 0) {
      return true;
    }
    const filtered = group.filter((item) =>
      item.propertyLabel.toLowerCase().includes(filterString.toLowerCase()),
    );
    return filtered.length > 0;
  }

  onLanguageChanged(language: Language) {
    this.currentLanguageId = language.id;
    this.onFormLanguageChanged.emit(language);
  }

  onTabChanged(event: TabChangeEvent) {
    //console.log('tab changed', tab);
    this.tabState[event.tabIndex] = event.activeTabName;
  }

  /**
   * Handles the change event for a checkbox in an array property.
   * If the checkbox is checked, a new control is added to the form array.
   * If the checkbox is unchecked, the corresponding control is removed from the form array.
   *
   * @param event - The change event object.
   * @param propertyName - The name of the array property.
   */
  onCheckArrayChange(
    checked: boolean,
    propertyName: string,
    rawValue: string | number,
  ) {
    const formArray: FormArray = this.formGroup.get([
      propertyName,
    ]) as FormArray;

    // if it’s all digits, parse it — otherwise keep it as-is
    const value: string | number = /^\d+$/.test(String(rawValue))
      ? parseInt(String(rawValue), 10)
      : rawValue;

    /* Selected */
    if (checked) {
      // Add a new control in the arrayForm

      formArray.push(new FormControl(value));
    } else {
      /* unselected */
      // find the unselected element
      let i: number = 0;

      formArray.controls.forEach((ctrl: FormControl) => {
        if (ctrl.value == value) {
          // Remove the unselected element from the arrayForm
          formArray.removeAt(i);
          return;
        }

        i++;
      });
    }
  }

  isStructuredMetricChecked(propertyName: string, value: string): boolean {
    const controlValue = this.formGroup?.get([propertyName])?.value as
      | StructuredMetricItem[]
      | null
      | undefined;
    if (!Array.isArray(controlValue)) return false;

    return controlValue.some(
      (item) => item?.property_uri === value && !!item?.selected,
    );
  }

  onStructuredCheckArrayChange(
    checked: boolean,
    propertyName: string,
    value: string,
    options: Array<{ value: string }>,
  ): void {
    const isChecked = checked;
    const formArray = this.formGroup.get([propertyName]) as FormArray;
    const current = Array.isArray(formArray?.value)
      ? [...(formArray.value as StructuredMetricItem[])]
      : [];

    const selectedByUri = new Map<string, boolean>();
    current.forEach((item) => {
      if (item?.property_uri != null) {
        selectedByUri.set(item.property_uri, !!item.selected);
      }
    });
    selectedByUri.set(value, isChecked);

    // Walk options in the same order the user sees them so new items keep
    // their displayed position instead of snapping back to the raw options order.
    const sortedOptions = this.getSortedStructuredOptions(
      propertyName,
      options as Array<{ value: string; name?: string; icon?: any }>,
    );
    const normalized: StructuredMetricItem[] = sortedOptions.map(
      (option, index) => ({
        property_uri: option.value,
        order: index + 1,
        selected: selectedByUri.get(option.value) ?? false,
      }),
    );

    if (!formArray) {
      return;
    }

    formArray.clear();
    normalized.forEach((item) => {
      formArray.push(new FormControl(item));
    });

    formArray.markAsDirty();
    formArray.markAsTouched();
    formArray.updateValueAndValidity();
  }

  getSortedStructuredOptions(
    propertyName: string,
    options: Array<{ value: string; name?: string; icon?: any }>,
  ): Array<{ value: string; name?: string; icon?: any }> {
    if (!Array.isArray(options)) return [];

    const controlValue = this.formGroup?.get([propertyName])?.value as
      | StructuredMetricItem[]
      | null
      | undefined;

    const orderByUri = new Map<string, number>();
    if (Array.isArray(controlValue)) {
      controlValue.forEach((item) => {
        if (item?.property_uri != null) {
          orderByUri.set(item.property_uri, item.order);
        }
      });
    }

    const fallbackBase = orderByUri.size + 1;
    return [...options].sort((a, b) => {
      const orderA = orderByUri.has(a.value)
        ? orderByUri.get(a.value)
        : fallbackBase + options.indexOf(a);
      const orderB = orderByUri.has(b.value)
        ? orderByUri.get(b.value)
        : fallbackBase + options.indexOf(b);
      return orderA - orderB;
    });
  }

  onStructuredCheckArrayDrop(
    event: CdkDragDrop<unknown>,
    propertyName: string,
    options: Array<{ value: string; name?: string; icon?: any }>,
  ): void {
    if (event.previousIndex === event.currentIndex) return;

    const formArray = this.formGroup.get([propertyName]) as FormArray;
    if (!formArray) return;

    const sortedOptions = this.getSortedStructuredOptions(
      propertyName,
      options,
    );
    moveItemInArray(sortedOptions, event.previousIndex, event.currentIndex);

    const currentByUri = new Map<string, StructuredMetricItem>();
    const currentValue = Array.isArray(formArray.value)
      ? (formArray.value as StructuredMetricItem[])
      : [];
    currentValue.forEach((item) => {
      if (item?.property_uri != null) {
        currentByUri.set(item.property_uri, item);
      }
    });

    const normalized: StructuredMetricItem[] = sortedOptions.map(
      (option, index) => {
        const existing = currentByUri.get(option.value);
        return {
          property_uri: option.value,
          order: index + 1,
          selected: existing?.selected ?? false,
        };
      },
    );

    formArray.clear();
    normalized.forEach((item) => {
      formArray.push(new FormControl(item));
    });

    formArray.markAsDirty();
    formArray.markAsTouched();
    formArray.updateValueAndValidity();
  }

  principalPicker(property: FormProperty) {
    const selectedObjectIds = new Map<string, ObjectDefinition>();
    const principalObjectClassIds = this.formGroup.get([
      property.propertyName,
    ]).value;

    this.addSelectedObjectIds(principalObjectClassIds, selectedObjectIds);
    const objectClassIds = [NomObjectClass.USER, NomObjectClass.USER_GROUP];

    this.store
      .select(selectUser)
      .pipe(
        take(1),
        switchMap(() => {
          const dialogRef = this.dialogService.openTabsDialog(
            PickerDataobjectsDialogComponent,
            {
              data: {
                selectedObjectIds,
                objectClassIds,
                useObjectMap: true,
                popupType: PopupType.SELECT_PRINCIPALS,
              },
              width: '61vw',
              minWidth: 1220,
              maxWidth: 1550,
              height: '70vh',
              closeOnClick: false,
              closeOnEscape: true,
            },
          );

          return dialogRef.closed;
        }),
        filter(
          (event: any) =>
            event?.reason === DialogCloseReason.Save ||
            event?.reason === DialogCloseReason.SaveAndClose,
        ),
        map((event: any) => event.data as Array<ObjectDefinition>),
        tap((objectsMap) => {
          this.formGroup.patchValue({
            [property.propertyName]: objectsMap,
          });
        }),
        switchMap((objectsMap) => {
          return this.utilsDataObjectApi
            .getPrincipalsByIds(objectsMap)
            .pipe(
              map((response) => ({ principals: response.items, objectsMap })),
            );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ principals, objectsMap }) => {
        if (!property.additionalConfig) {
          property.additionalConfig = {};
        }

        property.additionalConfig['principals'] = principals;
        property.additionalConfig['principalsToDisplay'] = principals;

        if (property.additionalConfig['subject$']) {
          property.additionalConfig['subject$'].next(objectsMap);
        }
      });
  }

  private addSelectedObjectIds(
    value: any,
    selectedObjectIds: Map<string, ObjectDefinition>,
  ) {
    if (value) {
      if (Array.isArray(value)) {
        value.forEach((v) =>
          selectedObjectIds.set(v, {
            object_id: v,
            object_class_id: null, // Default class, can be customized
          }),
        );
      } else {
        selectedObjectIds.set(value, {
          object_id: value,
          object_class_id: null, // Default class, can be customized
        });
      }
    }
  }

  dataObjectPicker(
    property: FormProperty,
    singleSelectionActive: boolean = false,
  ) {
    const selectedObjectIds = new Map<string, ObjectDefinition>();
    this.addSelectedObjectIds(
      this.formGroup.get([property.propertyName]).value,
      selectedObjectIds,
    );

    const objectClassIds = property.additionalConfig?.['objectClassIds'] || [
      NomObjectClass.FEED,
    ];
    const objectTypeIds = property.additionalConfig?.['objectTypeIds'] || [];

    const dialogRef = this.dialogService.openTabsDialog(
      PickerDataobjectsDialogComponent,
      {
        data: {
          objectClassIds,
          selectedObjectIds,
          objectTypeIds,
          singleSelectionActive,
          popupType: property.additionalConfig?.['popupType'] ?? null,
        },
        width: '61vw',
        minWidth: 1220,
        maxWidth: 1550,
        height: '70vh',
        closeOnClick: false,
        closeOnEscape: true,
      },
    );

    dialogRef.closed
      .pipe(
        take(1),
        filter(
          (event: any) =>
            event?.reason === DialogCloseReason.Save ||
            event?.reason === DialogCloseReason.SaveAndClose,
        ),
        tap((event: any) => {
          const objectIds = event.data;
          this.formGroup.patchValue({ [property.propertyName]: objectIds });
        }),
        switchMap((event: any) => {
          const objectIds = event.data;

          return this.userObjects
            .objectsByIds$(objectIds)
            .pipe(map((objects) => ({ objects, objectIds })));
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ objects, objectIds }) => {
        property.additionalConfig = {
          ...property.additionalConfig,
          objectsToDisplay: objects,
        };

        if (property.additionalConfig['subject$']) {
          property.additionalConfig['subject$'].next(objectIds);
        }
      });
  }

  /**
   * Handles the click event when a button is selected.
   *
   * @param prop - The form property associated with the button.
   * @param button - The selected button.
   */
  onSelectButtonClick(prop: FormProperty, button: any) {
    const selectedValue = this.formGroup.get([prop.propertyName]).value;
    button.action(selectedValue);
  }

  onButtonClick(property: FormProperty) {
    property.action();
  }

  setupPickers() {
    this.formProperties.forEach((prop) => {
      if (prop.controlType === 'dataobject-picker') {
        this.initializePicker(
          prop,
          'objects',
          'objectsToDisplay',
          prop.additionalConfig?.['objects$'],
        );
      }

      if (prop.controlType === 'principal-picker') {
        this.initializePicker(
          prop,
          'principals',
          'principalsToDisplay',
          prop.additionalConfig?.['principals$'],
        );
      }
    });
  }

  private initializePicker(
    prop: any,
    sourceKey: string,
    displayKey: string,
    observable$: Observable<any> | undefined,
  ): void {
    // Set up the default display array
    prop.additionalConfig[displayKey] ??= [];

    // If static source data exists, assign it to the display array
    if (prop.additionalConfig[sourceKey]) {
      prop.additionalConfig[displayKey] = prop.additionalConfig[sourceKey];
    }

    // Subscribe to the observable for dynamic updates
    if (observable$) {
      observable$
        .pipe(
          tap((objects) => {
            prop.additionalConfig[displayKey] = objects;
          }),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    }
  }

  /**
   * Updates the validators and validity of form controls based on the form properties.
   */
  estimateFormControlsValidators() {
    this.formProperties.forEach((prop) => {
      const control = this.formGroup.get([prop.propertyName]);
      if (control) {
        if (prop.validateBy) {
          const validators = prop.validateBy(this.formGroup);
          control.setValidators(validators);
        }
        control.updateValueAndValidity({ onlySelf: false, emitEvent: false });
      }
    });
    // trigger change detection
  }

  getPassword(property): string {
    return this.formGroup?.get([property])?.value;
  }

  ensureColorIndexControls(
    form: FormGroup,
    base: string, // e.g. 'settings.slice_color_id'
    length: number,
    defaultColor: string,
  ) {
    // Add missing controls
    for (let i = 0; i < length; i++) {
      const key = `${base}.${i}`;
      if (!form.get(key)) {
        form.addControl(key, new FormControl(defaultColor));
      }
    }
    // Remove extras
    const prefix = `${base}.`;
    Object.keys(form.controls)
      .filter((k) => k.startsWith(prefix))
      .forEach((k) => {
        const idx = Number(k.slice(prefix.length));
        if (!Number.isFinite(idx) || idx < 0 || idx >= length) {
          form.removeControl(k);
        }
      });
  }

  range(n: number): number[] {
    return Array.from({ length: Math.max(0, Number(n) || 0) }, (_, i) => i);
  }

  getArrayLen(prop: FormControlItem): number {
    // derive from configured driver, or count existing matching keys
    const lengthFrom = prop.additionalConfig['lengthFrom'] as
      | string
      | undefined;
    if (lengthFrom) {
      const value = this.formGroup.get([lengthFrom])?.value;

      return value || 10;
    }
    const prefix = `${prop.propertyName}.`;
    return Object.keys(this.formGroup.controls).filter((k) =>
      k.startsWith(prefix),
    ).length;
  }

  private updateCheckAllState(selectedValues: string[], options: any[]) {
    if (!selectedValues || !options) return;

    const optionsValues = options.map((option) => option.value);

    this.checkAllSelected =
      selectedValues.length === optionsValues.length &&
      optionsValues.every((val) => selectedValues.includes(val));
  }

  checkAll(
    checked: boolean,
    propertyName: string,
    options: Array<{ value: any }>,
  ): void {
    const formArray = this.formGroup.get([
      propertyName,
    ]) as FormArray<FormControl>;
    if (!formArray || !options) return;

    const allValues = options.map((o) => o.value);

    if (checked) {
      // Add any missing values (no duplicates)
      const current = new Set(formArray.value);
      allValues.forEach((v) => {
        if (!current.has(v)) {
          formArray.push(new FormControl(v));
        }
      });
    } else {
      // Remove only values that belong to this options list
      for (let i = formArray.length - 1; i >= 0; i--) {
        const v = formArray.at(i).value;
        if (allValues.includes(v)) {
          formArray.removeAt(i);
        }
      }
    }

    // Sync the "select all" flag
    const selectedValues: any[] = formArray.value;
    this.checkAllSelected =
      allValues.length > 0 &&
      allValues.every((v) => selectedValues.includes(v));

    formArray.markAsDirty();
    formArray.markAsTouched();
    formArray.updateValueAndValidity();
  }

  private getPropControlNames(prop: any): string[] {
    // If i18n: use the mapped control names; otherwise fallback to propertyName
    if (prop?.propertyNameLanguages) {
      return Object.values(prop.propertyNameLanguages).filter(
        Boolean,
      ) as string[];
    }
    return prop?.propertyName ? [prop.propertyName] : [];
  }

  private getControlsForProp(prop: any): AbstractControl[] {
    const names = this.getPropControlNames(prop);
    return names
      .map((n) => this.formGroup?.get([n]))
      .filter((c): c is AbstractControl => !!c);
  }

  anyTouched(prop: any): boolean {
    return this.getControlsForProp(prop).some((c) => c.touched);
  }

  anyInvalid(prop: any): boolean {
    return this.getControlsForProp(prop).some((c) => c.invalid);
  }

  hasAnyError(prop: any, errorKey: string): boolean {
    return this.getControlsForProp(prop).some((c) => !!c.errors?.[errorKey]);
  }

  hasAnyNestedError(prop: any, path: string[]): boolean {
    return this.getControlsForProp(prop).some((c) => {
      let cur: any = c.errors;
      for (const key of path) cur = cur?.[key];
      return !!cur;
    });
  }

  onControlChange(prop: FormProperty, event: any) {
    if (prop.onChange && typeof prop.onChange === 'function') {
      prop.onChange(event, this.formGroup);
    }
  }

  /**
   * Whether a `select` control exposes the clear (✕) affordance.
   * Non-clearable by default — the overwhelming majority of selects keep a
   * committed value. A field opts in explicitly with
   * `additionalConfig.clearable: true`. clearable and required remain
   * independent capabilities; this only chooses the default.
   */
  isSelectClearable(prop: any): boolean {
    return prop?.additionalConfig?.clearable === true;
  }

  checkGroupAll(checked: boolean, propertyName: string, groupOptions: any[]) {
    const isChecked = checked;
    const control = this.formGroup.get([propertyName]) as FormArray;

    if (!control) return;

    let currentValues = new Set<any>(control.value || []);

    groupOptions.forEach((option) => {
      if (isChecked) {
        currentValues.add(option.value);
      } else {
        currentValues.delete(option.value);
      }
    });

    control.clear();

    currentValues.forEach((val) => {
      control.push(new FormControl(val));
    });

    control.markAsDirty();
    control.markAsTouched();
  }

  // Helper to check if all items in a group are selected
  isGroupAllSelected(propertyName: string, groupOptions: any[]): boolean {
    const selectedValues = this.formGroup.get([propertyName])?.value || [];
    return groupOptions.every((opt) => selectedValues.includes(opt.value));
  }
}
